import { supabase } from '../lib/supabase';
import type { Json } from '../types/supabase';

/**
 * FR-22 — the events the app reports itself.
 *
 * Data changes are NOT logged from here: triggers on the tables do that
 * (migration 015), so they cannot be skipped. This is only for activity that
 * changes no row. Must match the list in log_audit_event().
 */
export type AuditAction =
  | 'auth.sign_in'
  | 'auth.sign_out'
  | 'patient.view'
  | 'export.download'
  | 'report.download';

/** Long enough for a slow link, short enough that sign-out never hangs on it. */
const AUDIT_TIMEOUT_MS = 3000;

/**
 * Records an event. Never throws, never blocks longer than AUDIT_TIMEOUT_MS.
 *
 * An audit write failing — offline, or the migration not applied yet — must
 * not stop a doctor opening a patient or a parent signing out. The trade-off
 * is that an offline action goes unlogged; data changes made offline are
 * still logged, by the triggers, when the queue syncs.
 */
export async function logAuditEvent(
  action: AuditAction,
  patientId: string | null = null,
  details: Record<string, Json> = {}
): Promise<void> {
  try {
    const result = await Promise.race([
      supabase.rpc('log_audit_event', {
        p_action: action,
        p_patient_id: patientId,
        p_details: details
      }),
      new Promise<null>(resolve => setTimeout(() => resolve(null), AUDIT_TIMEOUT_MS))
    ]);
    if (result && (result as { error?: unknown }).error) {
      console.warn('Audit event not recorded:', action, (result as { error: unknown }).error);
    }
  } catch (err) {
    console.warn('Audit event not recorded:', action, err);
  }
}
